import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { RefreshCcw, Clock, AlertCircle, CheckCircle, XCircle, HelpCircle, Mail } from 'lucide-react';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <RefreshCcw className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold">Refund & Cancellation Policy</h1>
            <p className="text-muted-foreground">Last updated: January 11, 2026</p>
          </div>

          {/* Quick Summary */}
          <Card className="border-emerald-500/30 bg-emerald-500/5">
            <CardContent className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Quick Summary
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 bg-background rounded-lg">
                  <p className="font-semibold text-emerald-600">Within 7 Days</p>
                  <p className="text-muted-foreground">Full refund (100%)</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <p className="font-semibold text-amber-600">8-14 Days</p>
                  <p className="text-muted-foreground">50% refund</p>
                </div>
                <div className="p-4 bg-background rounded-lg">
                  <p className="font-semibold text-red-600">After 14 Days</p>
                  <p className="text-muted-foreground">No refund</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-8 prose prose-gray dark:prose-invert max-w-none">
              <section className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Clock className="w-6 h-6 text-amber-500" />
                    1. Refund Eligibility
                  </h2>
                  
                  <h3 className="text-xl font-semibold mt-4">1.1 Self-Learning Programs</h3>
                  <table className="w-full mt-4 text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Timeframe</th>
                        <th className="text-left py-2">Refund Amount</th>
                        <th className="text-left py-2">Conditions</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b">
                        <td className="py-3">Within 7 days of purchase</td>
                        <td className="py-3 text-emerald-600 font-semibold">100% refund</td>
                        <td className="py-3">Less than 20% content accessed</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3">8-14 days of purchase</td>
                        <td className="py-3 text-amber-600 font-semibold">50% refund</td>
                        <td className="py-3">Less than 30% content accessed</td>
                      </tr>
                      <tr>
                        <td className="py-3">After 14 days</td>
                        <td className="py-3 text-red-600 font-semibold">No refund</td>
                        <td className="py-3">-</td>
                      </tr>
                    </tbody>
                  </table>

                  <h3 className="text-xl font-semibold mt-6">1.2 Instructor-Led Programs</h3>
                  <table className="w-full mt-4 text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Timeframe</th>
                        <th className="text-left py-2">Refund Amount</th>
                        <th className="text-left py-2">Conditions</th>
                      </tr>
                    </thead>
                    <tbody className="text-muted-foreground">
                      <tr className="border-b">
                        <td className="py-3">Before batch starts</td>
                        <td className="py-3 text-emerald-600 font-semibold">100% refund</td>
                        <td className="py-3">Full refund minus ₹500 processing fee</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3">Within first week of batch</td>
                        <td className="py-3 text-emerald-600 font-semibold">75% refund</td>
                        <td className="py-3">Attended less than 2 sessions</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3">Week 2-4 of batch</td>
                        <td className="py-3 text-amber-600 font-semibold">50% refund</td>
                        <td className="py-3">Subject to review</td>
                      </tr>
                      <tr>
                        <td className="py-3">After week 4</td>
                        <td className="py-3 text-red-600 font-semibold">No refund</td>
                        <td className="py-3">Batch transfer may be available</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <XCircle className="w-6 h-6 text-red-500" />
                    2. Non-Refundable Items
                  </h2>
                  <p className="text-muted-foreground mt-4">
                    The following are NOT eligible for refunds:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-2">
                    <li>Courses purchased during promotional sales (unless explicitly stated)</li>
                    <li>Courses where more than 50% content has been accessed</li>
                    <li>Certificate re-issuance fees</li>
                    <li>Courses purchased with coupon codes offering more than 50% discount</li>
                    <li>Accounts terminated due to Terms of Service violations</li>
                    <li>Degree programs (B.Des) after enrollment confirmation</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                    3. How to Request a Refund
                  </h2>
                  <ol className="list-decimal pl-6 space-y-3 text-muted-foreground mt-4">
                    <li>
                      <strong>Submit Request:</strong> Email us at <span className="text-emerald-500">refunds@visualinstituteofarts.com</span> with:
                      <ul className="list-disc pl-6 mt-2 space-y-1">
                        <li>Your registered email address</li>
                        <li>Course name and enrollment ID</li>
                        <li>Reason for refund request</li>
                        <li>Payment transaction ID</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Review Period:</strong> We will review your request within 3-5 business days
                    </li>
                    <li>
                      <strong>Approval Notification:</strong> You will receive an email confirming approval or rejection with reasons
                    </li>
                    <li>
                      <strong>Refund Processing:</strong> Approved refunds are processed within 7-10 business days
                    </li>
                  </ol>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">4. Refund Methods</h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li><strong>Original Payment Method:</strong> Refunds are credited to the original payment source</li>
                    <li><strong>UPI/Net Banking:</strong> 5-7 business days</li>
                    <li><strong>Credit/Debit Card:</strong> 7-10 business days (depends on bank)</li>
                    <li><strong>Wallet:</strong> 2-3 business days</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <HelpCircle className="w-6 h-6 text-blue-500" />
                    5. Special Circumstances
                  </h2>
                  
                  <h3 className="text-xl font-semibold mt-4">5.1 Batch Transfer (Instructor-Led Only)</h3>
                  <p className="text-muted-foreground mt-2">
                    Instead of a refund, you may request to transfer to a future batch (subject to availability). One free transfer is allowed; subsequent transfers incur a ₹2,000 fee.
                  </p>

                  <h3 className="text-xl font-semibold mt-4">5.2 Medical/Emergency Situations</h3>
                  <p className="text-muted-foreground mt-2">
                    For documented medical emergencies or exceptional circumstances, we may offer:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                    <li>Extended refund window</li>
                    <li>Course freeze (pause enrollment for up to 6 months)</li>
                    <li>Transfer to a family member</li>
                  </ul>

                  <h3 className="text-xl font-semibold mt-4">5.3 Course Cancellation by VIA</h3>
                  <p className="text-muted-foreground mt-2">
                    If we cancel a course or batch due to insufficient enrollment or other reasons:
                  </p>
                  <ul className="list-disc pl-6 space-y-1 text-muted-foreground mt-2">
                    <li>Full refund will be provided</li>
                    <li>Option to transfer to an alternative course</li>
                    <li>Credit for future enrollment with 10% bonus</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold">6. Coupon Codes & Promotional Pricing</h2>
                  <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                    <li>Refunds for discounted purchases are based on the amount actually paid</li>
                    <li>Coupon codes cannot be reinstated after refund</li>
                    <li>Flash sale purchases may have different refund terms (stated at time of purchase)</li>
                  </ul>
                </div>

                <div>
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Mail className="w-6 h-6 text-emerald-500" />
                    7. Contact for Refund Queries
                  </h2>
                  <div className="mt-4 p-4 bg-muted rounded-lg">
                    <p className="font-semibold">Refund Support Team</p>
                    <p className="text-muted-foreground mt-2">
                      <strong>Email:</strong> refunds@visualinstituteofarts.com<br />
                      <strong>Phone:</strong> +91 98765 43210 (Mon-Fri, 10 AM - 6 PM IST)<br />
                      <strong>Response Time:</strong> Within 24-48 hours
                    </p>
                  </div>
                </div>

                <div className="border-t pt-6 mt-8">
                  <div className="flex items-start gap-3 p-4 bg-amber-500/10 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <strong>Important:</strong> This refund policy is subject to change. The policy applicable at the time of your purchase will govern your refund eligibility. Always check the current policy before making a purchase.
                    </p>
                  </div>
                </div>
              </section>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
